import { useCallback, useRef, useState } from "react";
import {
  createVetJob as defaultCreateJob,
  pollVetJob as defaultPollJob,
  cancelVetJob as defaultCancelJob,
} from "./api";
import digestStoryIdea from "./verdictDigest";

// Drives the AI vet from the client side. The vet is ASYNC: the server splits
// the candidate pool into batches and runs the OpenAI calls on Sidekiq workers
// (off the web dynos — a sync call held a Puma thread per batch for up to 60s,
// risking router timeouts under concurrent large vets). The client POSTs the
// whole pool once, gets a job_id, then polls for incremental verdicts.
//
// Lifecycle: createVetJob → poll every POLL_INTERVAL_MS → merge each newly-seen
// verdict into onVerdictsChange (progressive render preserved) → stop on a
// terminal status. Cancel is a REAL server-side stop (the DELETE sets a flag
// the job checks before each batch's OpenAI call) plus a local poll-abort — it
// is re-implemented here, not inherited from the old fetch-AbortController.
//
// Partial-failure tolerance: batches that fail surface in failedBatches as
// { candidates, error } so the caller can render "Vet failed for N — retry?".
// The failed candidate objects are reconstructed from the pool this hook still
// holds, so Retry can re-vet exactly them.
const BIO_SNIPPET_MAX_CHARS = 200;
export const POLL_INTERVAL_MS = 1500;

// No-progress watchdog: how long we'll keep polling with NO new verdicts
// before giving up on a job that never reaches a terminal status (a dead
// worker that never fires the batch callback). Reset whenever progress
// advances. The per-batch budget must exceed a single batch's worst-case wall
// time (the server's 120s OpenAI request timeout) so a legitimately-slow batch
// can't trip the watchdog — otherwise we'd just move the false-failure from
// the server back onto the client. ~180s per 200-candidate batch + slack.
function maxPollMsFor(total) {
  const batches = Math.max(1, Math.ceil(total / 200));
  return (batches * 180 + 60) * 1000;
}

// Build the request shape the vet endpoint expects, truncating bio client-side
// (the server truncates again, but this shrinks the request body).
function toVetCandidate(c) {
  return {
    id: c.id,
    name: c.name,
    headline: c.headline,
    workplace: c.workplace,
    bio: (c.bio || "").slice(0, BIO_SNIPPET_MAX_CHARS),
  };
}

// Wrap server verdict items into the StoryIdea#ai_verdicts shape:
// { "<uuid>": { verdict, reason, model, vetted_at, vetted_against }, ... }.
// `model` is the server's (source of truth); `vetted_at` is stamped here at
// first-merge; `vetted_against` is the digest of the prompt-inputs the verdict
// was judged against — staleness is computed per-verdict against the current
// inputs' digest. Stamped client-side (not server-side) so a story-idea edit
// mid-job can't mis-attribute the digest.
function shapeVerdictsForStorage(verdicts, model, vettedAgainst) {
  const vettedAt = new Date().toISOString();
  return verdicts.reduce((acc, v) => {
    acc[v.user_id] = {
      verdict: v.verdict,
      reason: v.reason,
      model,
      vetted_at: vettedAt,
      vetted_against: vettedAgainst,
    };
    return acc;
  }, {});
}

// setTimeout as a promise that resolves early (rather than hanging) when the
// signal aborts, so cancel stops the poll loop promptly. The abort listener is
// removed once the sleep settles either way — without this, every tick where
// the timer wins would leave its listener attached to the run-scoped signal,
// leaking one listener per poll over a long vet.
function abortableSleep(ms, signal) {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    let timer;
    // {once:true} detaches this listener when abort fires, so the abort path
    // self-cleans; the timer path detaches the never-fired listener itself.
    function onAbort() {
      clearTimeout(timer);
      resolve();
    }
    timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled"]);

export default function useVerdictBatching({
  storyIdea,
  verdicts,
  onVerdictsChange,
  createJobFn = defaultCreateJob,
  pollJobFn = defaultPollJob,
  cancelJobFn = defaultCancelJob,
}) {
  const [vetting, setVetting] = useState(false);
  // progress: { completed, total } in candidates, so the UI can render
  // "Vetting 437 of 600..." — fed by the server snapshot's progress.
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  // Each entry: { candidates: [...], error } — drives the "retry?" affordance.
  const [failedBatches, setFailedBatches] = useState([]);

  // Latest verdicts in a ref, used to SEED a run's accumulator (so a re-vet
  // builds on existing verdicts). Accumulation during a run is held in a local
  // (see `vet`), not this ref, so a progress re-render resetting the ref from
  // the prop can't clobber mid-run accumulation if the parent hasn't yet
  // round-tripped the new verdicts back in.
  const verdictsRef = useRef(verdicts);
  verdictsRef.current = verdicts;

  // AbortController for the in-flight poll loop; cancel() aborts it.
  const abortRef = useRef(null);
  // The current job_id, so cancel() can tell the server to stop.
  const jobIdRef = useRef(null);

  const vet = useCallback(
    async (candidates) => {
      if (!candidates || candidates.length === 0) return;
      if (vetting) return;

      const controller = new AbortController();
      const { signal } = controller;
      abortRef.current = controller;
      setVetting(true);
      setFailedBatches([]);
      setProgress({ completed: 0, total: candidates.length });

      // Hold the pool by id so a failed batch's candidate_ids can be turned
      // back into full candidate objects for Retry.
      const byId = new Map(candidates.map((c) => [c.id, c]));
      const seenIds = new Set();
      // The deadline is a no-progress watchdog, not a hard SLA: it bounds how
      // long we'll wait with NO new verdicts (a dead/never-started job), and is
      // pushed forward whenever progress advances. So a legitimately slow vet
      // (batches queued behind other Sidekiq work) won't false-timeout and make
      // the admin re-pay for OpenAI calls that are still running server-side.
      let deadline = Date.now() + maxPollMsFor(candidates.length);
      let lastCompleted = 0;

      // Accumulate verdicts in a local seeded from current verdicts. Merge each
      // verdict only once (seenIds), stamping it at first-merge so a cumulative
      // poll response doesn't re-stamp vetted_at on every tick.
      let merged = { ...(verdictsRef.current || {}) };
      const mergeNewVerdicts = (snapshotVerdicts, model) => {
        const fresh = (snapshotVerdicts || []).filter((v) => !seenIds.has(v.user_id));
        if (fresh.length === 0) return;
        fresh.forEach((v) => seenIds.add(v.user_id));
        const shaped = shapeVerdictsForStorage(fresh, model, digestStoryIdea(storyIdea));
        merged = { ...merged, ...shaped };
        onVerdictsChange(merged);
      };

      const failPool = (poolCandidates, error) => setFailedBatches([{ candidates: poolCandidates, error }]);

      try {
        let jobId;
        try {
          ({ job_id: jobId } = await createJobFn({
            storyIdea,
            candidates: candidates.map(toVetCandidate),
          }));
        } catch (err) {
          // Couldn't even start — surface the whole pool as retryable.
          failPool(candidates, err);
          return;
        }
        jobIdRef.current = jobId;

        const failRemaining = (error) => {
          // Keep landed verdicts; surface the un-returned candidates as
          // retryable.
          const remaining = candidates.filter((c) => !seenIds.has(c.id));
          if (remaining.length > 0) failPool(remaining, error);
        };

        // Poll until terminal, aborted, or past the deadline. Poll FIRST (then
        // sleep between polls) so verdicts render without waiting a full
        // interval, and so a job that's already terminal needs no wait.
        while (!signal.aborted) {
          let snapshot = null;
          try {
            // eslint-disable-next-line no-await-in-loop
            snapshot = await pollJobFn(jobId, { signal });
          } catch (err) {
            if (err?.name === "AbortError" || signal.aborted) break;
            if (err?.notFound) {
              // Job state expired mid-poll — treat un-returned as retryable.
              failRemaining(err);
              break;
            }
            // Transient poll error — fall through to the sleep and retry.
          }

          if (snapshot) {
            mergeNewVerdicts(snapshot.verdicts, snapshot.model);
            const completed = snapshot.progress?.completed ?? seenIds.size;
            setProgress({ completed, total: snapshot.progress?.total ?? candidates.length });

            // Progress advanced → the job is alive; reset the watchdog against
            // the remaining work.
            if (completed > lastCompleted) {
              lastCompleted = completed;
              deadline = Date.now() + maxPollMsFor(candidates.length - completed);
            }

            if (TERMINAL_STATUSES.has(snapshot.status)) {
              const failed = (snapshot.failed || [])
                .map((f) => ({
                  candidates: (f.candidate_ids || []).map((id) => byId.get(id)).filter(Boolean),
                  error: new Error(f.error || "Vet failed"),
                }))
                .filter((f) => f.candidates.length > 0);
              if (failed.length > 0) setFailedBatches(failed);
              break;
            }
          }

          if (Date.now() > deadline) {
            // Never-terminal safety net (a dead worker that never fires the
            // batch callback).
            failRemaining(new Error("Vet timed out before completing"));
            break;
          }
          // eslint-disable-next-line no-await-in-loop
          await abortableSleep(POLL_INTERVAL_MS, signal);
        }
      } finally {
        setVetting(false);
        abortRef.current = null;
        jobIdRef.current = null;
      }
    },
    [vetting, storyIdea, createJobFn, pollJobFn, onVerdictsChange],
  );

  // Cancel the in-flight run: tell the server to stop issuing OpenAI calls
  // (real spend control), then abort the local poll loop. Already-applied
  // verdicts keep their values (they're real).
  const cancel = useCallback(() => {
    const jobId = jobIdRef.current;
    if (jobId) cancelJobFn(jobId).catch(() => {}); // best-effort
    abortRef.current?.abort();
  }, [cancelJobFn]);

  return { vet, cancel, vetting, progress, failedBatches };
}
